"use client";

import { useState } from "react";
import { Plus, Check, X } from "lucide-react";
import { formatTime, isOwnImage } from "@/lib/utils";
import { isActivity, FestivalAttraction } from "@/lib/types";
import { useSpotifyPlayer } from "../../spotify/spotify-player-context";
import { useScheduleBuilder } from "./schedule-context";
import { PerformanceModal } from "./performance-modal";
import type { ScheduleSlot } from "./use-schedule-data";
import { PlayIcon } from "@/components/spotify/play-icon";

export function ScheduleBlock({
  slot,
  dayStartMinutes,
  blockedSpotifyIds,
  festivalId,
  eventId,
  pixelsPerMinute = 2,
}: {
  slot: ScheduleSlot;
  dayStartMinutes: number;
  blockedSpotifyIds: string[];
  festivalId: number;
  eventId: number;
  pixelsPerMinute?: number;
}) {
  const { play, artistId: activeArtistId } = useSpotifyPlayer();
  const { savedSetKeys, toggleScheduleItem } = useScheduleBuilder();
  const [showModal, setShowModal] = useState(false);

  const top = (slot.startMinutes - dayStartMinutes) * pixelsPerMinute;
  const height = slot.durationMinutes * pixelsPerMinute;
  const isShort = height < 60;
  const isTiny = height < 40;

  const a = slot.attraction;
  const isAct = isActivity(a);
  const displayName = isAct
    ? a.activity_name
    : slot.attractions.length > 1
      ? `${a.name} +${slot.attractions.length - 1}`
      : a.name;

  const hasSpotify = !isAct && a.spotify_id && !blockedSpotifyIds.includes(a.spotify_id);
  const isPlaying = !isAct && hasSpotify && a.spotify_id === activeArtistId;
  const isSaved = !isAct && savedSetKeys.has(`${eventId}-${a.id}`);

  const hasImage = !isAct && a.image_url && isOwnImage(a.image_url);
  const showImage = hasImage && !isTiny;

  const genre = !isAct ? a.genres?.[0] : undefined;

  const blockClasses = `absolute left-0.5 right-0.5 group overflow-hidden rounded border transition-all
    ${
      isPlaying
        ? "border-[#1DB954]/50 z-[5]"
        : isSaved
          ? "border-acid/40 hover:border-acid/60 hover:z-10"
          : isAct
            ? "border-dashed border-neutral-700/50 hover:border-neutral-600 hover:z-10"
            : "border-neutral-700/50 hover:border-acid/50 hover:z-10 hover:shadow-[0_0_12px_rgba(209,248,5,0.15)]"
    }
    ${showImage ? "" : isPlaying ? "bg-[#1DB954]/10" : isSaved ? "bg-acid/5 hover:bg-acid/10" : isAct ? "bg-neutral-900/50" : "bg-neutral-900/80 hover:bg-neutral-800/90"}`;

  const blockContent = (
    <>
      {/* Background image with gradient overlay */}
      {showImage && !isAct && (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={(a as typeof a & { image_url: string }).image_url}
            alt={displayName}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className={`absolute inset-0 ${
            isPlaying
              ? "bg-gradient-to-t from-black/85 via-black/60 to-[#1DB954]/20"
              : isSaved
                ? "bg-gradient-to-t from-black/85 via-black/60 to-acid/10"
                : "bg-gradient-to-t from-black/90 via-black/70 to-black/40 group-hover:from-black/85 group-hover:via-black/55 group-hover:to-black/30"
          } transition-all`} />
        </>
      )}

      <div className={`relative flex flex-col h-full ${isTiny ? "px-1.5 py-0.5" : "p-1.5"} gap-0.5`}>
        {/* Time + Spotify — always on the same row */}
        {!isTiny && (
          <div className="flex items-center justify-between shrink-0">
            <span className={`font-mono text-[9px] leading-none ${showImage ? "text-neutral-300" : "text-neutral-500"}`}>
              {formatTime(a.set_time)}
            </span>
            <div className="flex items-center gap-0.5">
              {hasSpotify && !isAct && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    play((a as typeof a & { spotify_id: string }).spotify_id, displayName);
                  }}
                  className={`flex shrink-0 items-center justify-center cursor-pointer transition-colors
                    ${showImage
                      ? `h-5 w-5 rounded-full ${isPlaying ? "bg-[#1DB954] text-black" : "bg-black/50 text-white/80 hover:bg-[#1DB954] hover:text-black backdrop-blur-sm"}`
                      : `h-4 w-4 ${isPlaying ? "text-[#1DB954]" : "text-neutral-600 hover:text-[#1DB954]"}`
                    }`}
                >
                  <PlayIcon className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Name */}
        <span
          className={`font-mono leading-tight ${
            isAct ? "text-neutral-400 italic" : showImage ? "text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" : "text-neutral-200"
          } ${
            isTiny ? "text-[10px] truncate" : isShort ? "text-xs truncate" : "text-xs line-clamp-2"
          }`}
        >
          {displayName}
        </span>

        {/* Genre tag — only if tall enough */}
        {!isShort && genre && (
          <span className={`mt-auto rounded-full border px-1.5 py-0.5 font-mono text-[8px] w-fit leading-none ${
            showImage
              ? "border-white/20 text-white/70 bg-black/30 backdrop-blur-sm"
              : "border-acid/20 text-acid/60"
          }`}>
            {genre.toUpperCase()}
          </span>
        )}
      </div>

      {/* Add to schedule bar — only for artists */}
      {!isTiny && !isAct && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleScheduleItem(festivalId, eventId, (a as FestivalAttraction).id, displayName);
          }}
          className={`group/save absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 py-1 cursor-pointer transition-all font-mono text-[9px] font-bold tracking-wider
            ${
              isSaved
                ? "bg-acid/15 text-acid border-t border-acid/20 opacity-100 backdrop-blur-sm hover:bg-red-500/15 hover:text-red-400 hover:border-red-500/20"
                : "bg-black/80 text-neutral-500 border-t border-neutral-700/50 opacity-0 group-hover:opacity-100 hover:text-acid hover:bg-acid/10 backdrop-blur-sm"
            }`}
        >
          {isSaved ? (
            <>
              <Check className="h-3 w-3 group-hover/save:hidden" />
              <X className="h-3 w-3 hidden group-hover/save:block" />
              <span className="group-hover/save:hidden">SAVED</span>
              <span className="hidden group-hover/save:inline">REMOVE</span>
            </>
          ) : (
            <>
              <Plus className="h-3 w-3" />
              ADD TO SCHEDULE
            </>
          )}
        </button>
      )}
    </>
  );

  // Activities render as a plain div, artists as a clickable div that opens the modal
  if (isAct) {
    return (
      <div className={blockClasses} style={{ top: `${top}px`, height: `${height}px` }}>
        {blockContent}
      </div>
    );
  }

  return (
    <>
      <div
        onClick={() => setShowModal(true)}
        className={`${blockClasses} cursor-pointer`}
        style={{ top: `${top}px`, height: `${height}px` }}
        data-schedule-block
      >
        {blockContent}
      </div>
      {showModal && (
        <PerformanceModal
          slot={slot}
          festivalId={festivalId}
          eventId={eventId}
          blockedSpotifyIds={blockedSpotifyIds}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}
